/**
 * Execução do bloco de "código no navegador" nos dois modos.
 *
 * ## `sandbox` (padrão)
 * Web Worker criado de um Blob URL: sem `window`, sem `document`, sem cookies do site host. Mas
 * há um detalhe que costuma passar batido — **um worker de blob herda a origem da página host**.
 * Sem tratamento, o código do worker poderia chamar `fetch('/api/me', {credentials:'include'})`
 * e falar com o backend do próprio cliente autenticado como o visitante. Por isso o prelúdio do
 * worker apaga `fetch`, `XMLHttpRequest`, `WebSocket`, `importScripts` e companhia antes de
 * entregar o controle ao código do usuário. Aqui, `sandbox` significa **computação sem rede**;
 * quem precisa de rede usa o bloco de webhook, que é explícito sobre isso.
 *
 * O worker também é a única forma de ter timeout de verdade: `terminate()` interrompe até laço
 * infinito síncrono.
 *
 * ## `page`
 * `new Function` no contexto da página, com acesso total — o equivalente ao *Custom HTML* do GTM.
 * Só é seguro por causa do vínculo com o domínio: o servidor não entrega a config fora da
 * `authorizedDomains`. Limite honesto: aqui o timeout **não** interrompe código síncrono; um
 * `while(true)` congela a aba do visitante. É mais um motivo para o padrão ser `sandbox`.
 */

import type { JsonValue, Variables } from '@merlin/flow-engine';

export type CodeResult = { ok: true; value: JsonValue } | { ok: false; error: string };

/**
 * Prelúdio + laço de mensagens do worker. Fica como string porque precisa virar Blob — não há
 * arquivo separado para o navegador buscar, e assim o widget continua sendo um único `.js`.
 */
const WORKER_SOURCE = `
(function () {
  // Fecha os caminhos de rede: o worker herda a origem da página host.
  var blocked = ['fetch','XMLHttpRequest','WebSocket','EventSource','importScripts','indexedDB','caches','Notification','SharedWorker','Worker'];
  for (var i = 0; i < blocked.length; i++) {
    try { self[blocked[i]] = undefined; } catch (e) {}
  }
  try { if (self.navigator && self.navigator.sendBeacon) self.navigator.sendBeacon = undefined; } catch (e) {}

  function describe(error) {
    if (!error) return 'Erro desconhecido no código.';
    if (error.message) return String(error.message);
    return String(error);
  }

  // O valor precisa atravessar o postMessage e caber no JSON das variáveis do fluxo.
  function normalize(value) {
    if (value === undefined) return null;
    try { return JSON.parse(JSON.stringify(value)); } catch (e) { return String(value); }
  }

  self.onmessage = function (event) {
    var payload = event.data || {};
    try {
      var fn = new Function('vars', '"use strict";' + payload.code);
      Promise.resolve(fn(payload.variables || {})).then(
        function (value) { self.postMessage({ ok: true, value: normalize(value) }); },
        function (error) { self.postMessage({ ok: false, error: describe(error) }); }
      );
    } catch (error) {
      self.postMessage({ ok: false, error: describe(error) });
    }
  };
})();
`;

const CSP_WORKER_HINT =
  'O navegador bloqueou a criação do Web Worker. Normalmente é o Content-Security-Policy do site ' +
  '(`worker-src`/`script-src` sem `blob:`). Libere `blob:` para o widget ou troque este bloco para o modo `page`.';

const CSP_EVAL_HINT =
  'O navegador bloqueou a execução do código. Normalmente é o Content-Security-Policy do site ' +
  '(`script-src` sem `unsafe-eval`). Sem isso, o bloco de código não roda nesta página.';

function describeError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return String(error);
}

export function runInSandbox(code: string, variables: Variables, timeoutMs: number): Promise<CodeResult> {
  return new Promise<CodeResult>((resolve) => {
    let worker: Worker;
    let blobUrl = '';

    try {
      blobUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
      worker = new Worker(blobUrl);
    } catch (error) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      resolve({ ok: false, error: `${CSP_WORKER_HINT} (${describeError(error)})` });
      return;
    }

    let settled = false;
    const finish = (result: CodeResult): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
      resolve(result);
    };

    // Timeout real: `terminate()` interrompe inclusive laço síncrono dentro do worker.
    const timer = setTimeout(() => {
      finish({ ok: false, error: `O código não terminou em ${timeoutMs}ms e foi interrompido.` });
    }, timeoutMs);

    worker.onmessage = (event: MessageEvent<CodeResult>) => {
      const data = event.data;
      finish(
        data && typeof data === 'object' && 'ok' in data
          ? data
          : { ok: false, error: 'O worker devolveu um resultado inesperado.' },
      );
    };

    worker.onerror = (event: ErrorEvent) => {
      finish({ ok: false, error: event.message || 'Falha ao executar o código no sandbox.' });
    };

    try {
      worker.postMessage({ code, variables });
    } catch (error) {
      finish({ ok: false, error: `Não foi possível enviar as variáveis ao sandbox: ${describeError(error)}` });
    }
  });
}

export async function runInPage(code: string, variables: Variables, timeoutMs: number): Promise<CodeResult> {
  let fn: (vars: Variables) => unknown;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function('vars', `"use strict";${code}`) as (vars: Variables) => unknown;
  } catch (error) {
    const message = describeError(error);
    const isCsp = error instanceof EvalError || /unsafe-eval|Content Security Policy|CSP/i.test(message);
    return { ok: false, error: isCsp ? `${CSP_EVAL_HINT} (${message})` : message };
  }

  try {
    const value = await Promise.race([
      Promise.resolve(fn(variables)),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error(`O código não terminou em ${timeoutMs}ms.`)),
          timeoutMs,
        ),
      ),
    ]);
    if (value === undefined) return { ok: true, value: null };
    try {
      return { ok: true, value: JSON.parse(JSON.stringify(value)) as JsonValue };
    } catch {
      return { ok: true, value: String(value) };
    }
  } catch (error) {
    return { ok: false, error: describeError(error) };
  }
}

export interface RunCodeOptions {
  /**
   * Força o sandbox mesmo quando o bloco pede `page`. É o que o preview do painel usa: rodar
   * código de terceiros no contexto da própria ferramenta não vale o risco, e no preview não há
   * vínculo com o domínio do cliente para justificar o modo `page`.
   */
  forceSandbox?: boolean;
}

export async function runCode(
  effect: { code: string; executionMode: 'page' | 'sandbox'; timeoutMs: number },
  variables: Variables,
  options: RunCodeOptions = {},
): Promise<CodeResult> {
  const mode = options.forceSandbox ? 'sandbox' : effect.executionMode;
  return mode === 'page'
    ? runInPage(effect.code, variables, effect.timeoutMs)
    : runInSandbox(effect.code, variables, effect.timeoutMs);
}
